import React, { useEffect, useMemo } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { App, Flex } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { handleErrors } from "@/shared/utils/forms";
import {
  useCreateUserInviteMutation,
  usePatchUserInviteByIdMutation,
} from "@/redux/service";
import {
  OrganizationRoles,
  OrganizationRoleValue,
  Roles,
} from "@/shared/utils/constants";
import {
  HookFormBaseDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseModal from "@/components_v2/Modal/BaseModal";
import {
  CreateUpdateInviteFormValues,
  InviteFormModalProps,
} from "@/features/organizations/profile/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "../styles.css";

const InviteFormModal = ({
  open,
  setOpen,
  defaultData,
}: InviteFormModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const currentUser = useSelector(selectCurrentUser);
  const [createUserInvite] = useCreateUserInviteMutation();
  const [patchUserInviteById] = usePatchUserInviteByIdMutation();

  const inviteSchema: yup.ObjectSchema<CreateUpdateInviteFormValues> = yup
    .object()
    .shape({
      firstName: yup.string().typeError(t("form.labels.notValid")),
      lastName: yup.string().typeError(t("form.labels.notValid")),
      email: yup
        .string()
        .email(t("form.labels.notValid"))
        .typeError(t("form.labels.notValid"))
        .required(t("form.labels.required")),
      role: yup
        .string()
        .oneOf(
          OrganizationRoles.asList().map((i: OrganizationRoleValue) => i.value),
          t("form.labels.notValid"),
        )
        .typeError(t("form.labels.notValid"))
        .required(t("form.labels.required")),
      organization: yup
        .number()
        .typeError(t("form.labels.notValid"))
        .required(t("form.labels.required")),
    });

  const form = useForm<CreateUpdateInviteFormValues>({
    resolver: yupResolver(inviteSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: undefined,
      role: undefined,
      organization: undefined,
    },
  });

  useEffect(() => {
    if (defaultData) {
      form.reset(
        {
          firstName: defaultData.firstName,
          lastName: defaultData.lastName,
          email: defaultData.email,
          role: defaultData.role,
        },
        { keepDefaultValues: true },
      );
    }
    form.setValue("organization", defaultData?.organization);
  }, [open, defaultData]);

  const fields = useMemo(() => {
    if (!currentUser) return [];

    const roleOptions =
      currentUser.role === Roles.ADMIN.value
        ? OrganizationRoles.asList()
        : OrganizationRoles.asList().filter(
            (i: ReturnType<typeof OrganizationRoles.asList>[0]) =>
              i.value !== OrganizationRoles.ADMIN.value,
          );

    return [
      {
        label: t("organizations.labels.firstName"),
        required: false,
        field: (
          <HookFormInput
            name="firstName"
            control={form.control}
            placeholder={t("organizations.placeholders.enterFirstName")}
          />
        ),
      },
      {
        label: t("organizations.labels.lastName"),
        required: false,
        field: (
          <HookFormInput
            name="lastName"
            placeholder={t("organizations.placeholders.enterLastName")}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.email"),
        required: true,
        field: (
          <HookFormInput
            name="email"
            disabled={!!defaultData?.id}
            placeholder={t("organizations.placeholders.enterEmail")}
            control={form.control}
          />
        ),
      },
      {
        label: t("organizations.labels.role"),
        required: true,
        field: (
          <HookFormBaseDropdown
            name="role"
            placeholder={t("organizations.placeholders.selectRole")}
            options={roleOptions}
            control={form.control}
          />
        ),
      },
    ];
  }, [currentUser]);

  const onClose = () => {
    form.reset();
    setOpen(false);
  };

  const handleSubmit = (values: CreateUpdateInviteFormValues) => {
    if (defaultData?.id) {
      patchUserInviteById({ id: defaultData.id, data: values })
        .unwrap()
        .then(() => {
          notification.success({
            message: t("organizations.labels.userInviteUpdated"),
          });
          onClose();
        })
        .catch((errors) => handleErrors(errors, form.setError, notification));
    } else {
      createUserInvite(values)
        .unwrap()
        .then(() => {
          notification.success({
            message: t("organizations.labels.userInviteSent"),
          });
          onClose();
        })
        .catch((errors) => handleErrors(errors, form.setError, notification));
    }
  };

  return (
    <BaseModal
      destroyOnClose
      title={
        defaultData?.id
          ? t("organizations.labels.updateInvite")
          : t("organizations.labels.createInvite")
      }
      subtitle={
        defaultData?.id
          ? undefined
          : t("organizations.labels.inviteDescription")
      }
      open={open}
      onCancel={onClose}
      footer={
        <Flex wrap justify="space-between" align="center" gap={"small"}>
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            onClick={onClose}
          >
            {t("organizations.labels.cancel")}
          </BaseButton>

          <BaseButton
            variant="primary"
            size="small"
            loading={form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("organizations.labels.submit")}
          </BaseButton>
        </Flex>
      }
    >
      <div className="invite-form-modal-form-fields">
        {fields.map((field) => (
          <FormFieldWrapper
            key={field.label}
            label={field.label}
            required={field.required}
          >
            {field.field}
          </FormFieldWrapper>
        ))}
      </div>
    </BaseModal>
  );
};

export default InviteFormModal;
